'use client'

import { useState, useEffect, useCallback } from 'react';
import { registerPromptDialog } from '@/lib/notifications';

type PromptDialogOptions = {
  title: string;
  message: string;
  inputType?: 'text' | 'password' | 'email';
  placeholder?: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info' | 'success';
  required?: boolean;
  validator?: (value: string) => string | null;
  onConfirm: (value: string) => void | Promise<void>;
  onCancel: () => void;
};

export function PromptDialogProvider({ children }: { children: React.ReactNode }) {
  const [dialogOptions, setDialogOptions] = useState<PromptDialogOptions | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    registerPromptDialog((options) => {
      setDialogOptions(options);
      setInputValue(options.defaultValue || '');
      setError(null);
      setIsLoading(false);
    });
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!dialogOptions) return;

    // Validate required field
    if (dialogOptions.required && !inputValue.trim()) {
      setError('This field is required');
      return;
    }

    // Custom validation
    if (dialogOptions.validator) {
      const validationError = dialogOptions.validator(inputValue);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    setIsLoading(true);
    try {
      await Promise.resolve(dialogOptions.onConfirm(inputValue));
      setDialogOptions(null);
      setInputValue('');
      setError(null);
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [dialogOptions, inputValue]);

  const handleCancel = useCallback(() => {
    if (!dialogOptions) return;
    dialogOptions.onCancel();
    setDialogOptions(null);
    setInputValue('');
    setError(null);
  }, [dialogOptions]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleConfirm();
    } else if (e.key === 'Escape' && !isLoading) {
      handleCancel();
    }
  }, [handleConfirm, handleCancel, isLoading]);

  if (!dialogOptions) return <>{children}</>;

  const variantStyles = {
    danger: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
    warning: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500',
    info: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
    success: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
  };

  const confirmButtonStyle = variantStyles[dialogOptions.variant || 'info'];

  return (
    <>
      {children}
      
      {/* Modal Overlay */}
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {dialogOptions.title}
            </h3>
          </div>

          {/* Body */}
          <div className="px-6 py-4 space-y-4">
            {dialogOptions.message && (
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {dialogOptions.message}
              </p>
            )}

            <div>
              <input
                type={dialogOptions.inputType || 'text'}
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  setError(null);
                }}
                onKeyDown={handleKeyDown}
                placeholder={dialogOptions.placeholder}
                disabled={isLoading}
                autoFocus
                className={`
                  w-full px-3 py-2 
                  border rounded-md 
                  bg-white dark:bg-gray-700 
                  text-gray-900 dark:text-white
                  placeholder-gray-400 dark:placeholder-gray-500
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                  disabled:opacity-50 disabled:cursor-not-allowed
                  ${error ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-600'}
                `}
              />
              {error && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {error}
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-750 rounded-b-lg flex justify-end space-x-3">
            <button
              onClick={handleCancel}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {dialogOptions.cancelText || 'Cancel'}
            </button>
            <button
              onClick={handleConfirm}
              disabled={isLoading}
              className={`
                px-4 py-2 text-sm font-medium text-white rounded-md 
                focus:outline-none focus:ring-2 focus:ring-offset-2 
                disabled:opacity-50 disabled:cursor-not-allowed
                ${confirmButtonStyle}
              `}
            >
              {isLoading ? 'Loading...' : (dialogOptions.confirmText || 'OK')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
